import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Divider from 'LuxKit/Divider'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { capitalise } from 'lib/string/stringUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import useLuxPlusPreferredPaymentCard from 'luxPlus/hooks/useLuxPlusPreferredPaymentMethod'
import { getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import getLuxPlusCardExpiresBeforeRenewal from 'luxPlus/lib/getLuxPlusCardExpiresBeforeRenewal'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

const PreferredPaymentMethodStack = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(4)};
`

const PreferredPaymentMethodRow = styled.div<{ $hasWarning?: boolean }>`
  display: flex;
  min-width: 0;
  min-height: ${rem(40)};
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  padding: ${rem(8)};
  border: 1px solid ${props => props.$hasWarning ? props.theme.palette.messaging.critical.normalForeground : props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CardContent = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(12)};
`

const CardLogoFrame = styled.div`
  display: flex;
  width: ${rem(34)};
  height: ${rem(24)};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(4)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const CardLogo = styled.img`
  max-width: ${rem(24)};
  max-height: ${rem(16)};
`

const ExpiryWarningNotice = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.messaging.critical.normalForeground};
`

interface Props {
  onUpdatePaymentMethod?: () => void
  item: App.MemberSubscriptionItem
  renewalDate?: string
  refreshKey?: number
  showDivider?: boolean
}

function LuxPlusPreferredPaymentMethod({ onUpdatePaymentMethod, item, renewalDate, refreshKey, showDivider = true }: Props) {
  const intl = useIntl()
  const isPendingRenewalLuxPlusMember = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const nextChargeAttemptDate = item.nextChargeAttemptDate
  const finalChargeAttemptDate = item.finalChargeAttemptDate
  const hasAutoRenewal = item.renewalStatus === 'ALLOW_RENEWAL'

  const preferredPaymentCard = useLuxPlusPreferredPaymentCard(refreshKey)

  const card = preferredPaymentCard?.data
  const error = preferredPaymentCard?.error
  const isLoading = preferredPaymentCard?.fetching ?? false
  const hasNoCard = !card && error
  const cardExpiry = card?.expiry
  const expiresBeforeRenewal = getLuxPlusCardExpiresBeforeRenewal(card, renewalDate)

  return <>
    <VerticalSpacer gap={12}>
      <BodyText variant="medium" weight="bold">{intl.formatMessage({
        id: 'xiFr9Pr-SwrMzZAEXHYR',
        defaultMessage: 'Preferred payment method',
        description: 'Section heading for preferred payment method in LuxPlus+ account management',
      })}
      </BodyText>
      {isLoading && <TextLoadingBox width="100%" />}
      {!isLoading && <PreferredPaymentMethodStack>
        <PreferredPaymentMethodRow $hasWarning={expiresBeforeRenewal}>
          <CardContent>
            {card && <>
              {card.logo && <CardLogoFrame>
                <CardLogo
                  alt={intl.formatMessage(
                    {
                      id: '-AGhDg8G8LqN0VN4U8Oq',
                      defaultMessage: '{cardBrand} card ending with {lastFourDigits}',
                      description: 'Alt text for the payment card logo image in LuxPlus+ preferred payment method',
                    },
                    { cardBrand: card.brand, lastFourDigits: card.last4 },
                  )}
                  src={card.logo}
                />
              </CardLogoFrame>}
              <BodyText variant="small" weight="bold" wrap="no-wrap">
                {intl.formatMessage({
                  id: 'P2mePT9XTtKXxpuAKE3Y',
                  defaultMessage: '{brand} ******** {last4}',
                  description: 'Preferred payment card label in LuxPlus+ account management',
                }, { brand: capitalise(card.brand), last4: card.last4 })}
              </BodyText>
              {!!cardExpiry && <BodyText variant="small" wrap="no-wrap">
                {intl.formatMessage({
                  id: 'Cxa7UX-E0c9HJ3tIuFc6',
                  defaultMessage: 'Exp. {expiry}',
                  description: 'Label displaying expiration date for preferred card in LuxPlus+ account management',
                }, { expiry: cardExpiry })}
              </BodyText>}
            </>}
            {hasNoCard && <BodyText variant="small">{intl.formatMessage({
              id: '6tZJE1CGqt1ygDy64DIE',
              defaultMessage: 'No card provided',
              description: 'Fallback text when no payment card is on file for LuxPlus+ membership',
            })}
            </BodyText>}
          </CardContent>
          <TextLink size="caption" colour="neutral-one" onClick={onUpdatePaymentMethod}>{intl.formatMessage({
            id: 'GuU_YHtWQULB9OKHMgUW',
            defaultMessage: 'Update',
            description: 'Link text to update preferred payment method for LuxPlus+ membership',
          })}
          </TextLink>
        </PreferredPaymentMethodRow>
        {expiresBeforeRenewal && <ExpiryWarningNotice>
          <LineInfoCircleIcon size="XS" colour="critical" />
          <BodyText variant="small" colour="critical">
            {intl.formatMessage({
              id: 'djB2qpqGAWG963JYhTw1',
              defaultMessage: 'Expires before your next renewal',
              description: 'Warning text when a LuxPlus+ backup payment method expires before renewal',
            })}
          </BodyText>
        </ExpiryWarningNotice>}
      </PreferredPaymentMethodStack>}
    </VerticalSpacer>
    {(isPendingRenewalLuxPlusMember && hasAutoRenewal && nextChargeAttemptDate !== undefined) && <MessageBanner
      kind="warning"
      density="compact"
      heading={intl.formatMessage({
        id: 'JPhdUWWm9dJCVRHTz3QN',
        defaultMessage: 'Renewal attempt failed',
        description: 'Warning banner heading when LuxPlus+ membership renewal payment attempt fails',
      })}
    >
      <FormattedMessage
        id="WybwZUjRTQ1n2eZchTUr"
        defaultMessage="Initial payment failed. You will be charged on {chargeDate} on your preferred payment method above."
        description="Warning message when LuxPlus+ membership renewal payment fails, showing next charge attempt date"
        values={{
          chargeDate: <FormatDateTime value={nextChargeAttemptDate} dateStyle="medium" />,
        }}
      />
    </MessageBanner>}
    {showDivider && <Divider kind="primary" />}
    {(isPendingRenewalLuxPlusMember && hasAutoRenewal && finalChargeAttemptDate !== undefined) &&
      <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
        <BodyText variant="medium" weight="bold" colour="urgency">{intl.formatMessage({
          id: 'Zl0I4OHwMbuCp9_aUvIr',
          defaultMessage: 'Payment due',
          description: 'Label for payment due date when LuxPlus+ membership renewal is pending',
        })}
        </BodyText>
        <BodyText variant="medium" colour="urgency"><FormatDateTime value={finalChargeAttemptDate} dateStyle="medium" /></BodyText>
      </Group>}
  </>
}

export default LuxPlusPreferredPaymentMethod
